import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { addDoc, collection, deleteDoc, doc, orderBy, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { markSeen } from "../hooks/useUnseenBadges";
import { Sheet } from "../components/Sheet";
import type { CalendarEvent, Todo, TodoComment } from "../types";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isOverdue(todo: Todo): boolean {
  return !todo.done && !!todo.dueDate && todo.dueDate < todayISO();
}

export function Todos() {
  const { user } = useAuth();
  const { couple, profile, partner } = useCouple();
  const { items, loading, add, patch, remove } = useCoupleCollection<Todo>(
    couple?.id ?? null,
    "todos",
    orderBy("createdAt", "asc")
  );
  // Direct Firestore calls rather than useCoupleCollection — this only ever
  // creates/patches/deletes the one linked event for a given todo, and
  // useCoupleCollection would otherwise subscribe to (and re-render on
  // every change to) the *entire* calendarEvents collection just to get at
  // add/patch/remove, same reasoning as Mood.tsx and useLocationSharing.ts.
  const coupleId = couple?.id ?? null;
  async function addCalEvent(data: Omit<CalendarEvent, "id" | "createdAt">) {
    const ref = await addDoc(collection(db, "couples", coupleId!, "calendarEvents"), { ...data, createdAt: serverTimestamp() });
    return ref.id;
  }
  async function patchCalEvent(id: string, data: Partial<CalendarEvent>) {
    await updateDoc(doc(db, "couples", coupleId!, "calendarEvents", id), data);
  }
  async function removeCalEvent(id: string) {
    await deleteDoc(doc(db, "couples", coupleId!, "calendarEvents", id));
  }

  useEffect(() => {
    markSeen("todos");
    return () => markSeen("todos");
  }, []);

  // Always-visible instead of behind a Fab+Sheet tap — adding a todo is
  // frequent enough that the extra tap to open a sheet was pure friction.
  const [quickText, setQuickText] = useState("");
  const [quickSubmitting, setQuickSubmitting] = useState(false);

  async function handleQuickAdd(e: FormEvent) {
    e.preventDefault();
    if (quickSubmitting || !quickText.trim() || !user || !profile) return;
    setQuickSubmitting(true);
    try {
      await add({
        text: quickText.trim(),
        done: false,
        assignedToUid: null,
        dueDate: null,
        createdByUid: user.uid,
        createdByName: profile.displayName,
      });
      setQuickText("");
    } finally {
      setQuickSubmitting(false);
    }
  }

  // Editing (text + due date) stays in a Sheet — it's a less frequent action
  // than adding, so the extra tap doesn't cost much.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [showInCalendar, setShowInCalendar] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function openEdit(todo: Todo) {
    setEditingId(todo.id);
    setText(todo.text);
    setDueDate(todo.dueDate ?? "");
    setShowInCalendar(!!todo.linkedEventId);
    setSheetOpen(true);
  }

  function closeSheet() {
    setSheetOpen(false);
    setEditingId(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !text.trim() || !editingId || !user || !profile) return;
    setSubmitting(true);
    try {
      const finalDueDate = dueDate || null;
      const editingTodo = items.find((t) => t.id === editingId);
      let linkedEventId = editingTodo?.linkedEventId ?? null;

      if (showInCalendar && finalDueDate) {
        if (linkedEventId) {
          await patchCalEvent(linkedEventId, {
            title: text.trim(),
            startDate: finalDueDate,
            endDate: finalDueDate,
            lastEditedByUid: user.uid,
            lastEditedByName: profile.displayName,
          });
        } else {
          linkedEventId = await addCalEvent({
            title: text.trim(),
            startDate: finalDueDate,
            endDate: finalDueDate,
            time: null,
            endTime: null,
            notes: "",
            categoryId: null,
            recurrence: "none",
            recurrenceCount: null,
            linkedTodoId: editingId,
            createdByUid: user.uid,
            createdByName: profile.displayName,
          });
        }
      } else if (linkedEventId) {
        // Toggled off, or the due date was cleared — the calendar entry only
        // ever existed because of this todo, so it goes away with it rather
        // than being left behind as an orphaned, unlinked event.
        await removeCalEvent(linkedEventId).catch(() => {});
        linkedEventId = null;
      }

      await patch(editingId, { text: text.trim(), dueDate: finalDueDate, linkedEventId });
      closeSheet();
    } finally {
      setSubmitting(false);
    }
  }

  async function removeTodo(todo: Todo) {
    if (todo.linkedEventId) await removeCalEvent(todo.linkedEventId).catch(() => {});
    await remove(todo.id);
  }

  // Collapsed by default — a long "done" history otherwise buried the
  // actually-open todos further down the page every time you opened it.
  const [doneOpen, setDoneOpen] = useState(false);

  const [commentTodoId, setCommentTodoId] = useState<string | null>(null);
  const commentTodo = items.find((t) => t.id === commentTodoId) ?? null;

  // Arriving from the Neuigkeiten bell: ?comment=id opens that todo's
  // thread directly; ?open=id opens its edit sheet. The target todo might
  // not have loaded yet, so ?open= is applied once it actually shows up
  // in `items` rather than assumed present on mount.
  const [searchParams] = useSearchParams();
  const [pendingOpenId, setPendingOpenId] = useState(() => searchParams.get("open"));
  useEffect(() => {
    const commentParam = searchParams.get("comment");
    if (commentParam) setCommentTodoId(commentParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!pendingOpenId) return;
    const match = items.find((t) => t.id === pendingOpenId);
    if (match) {
      openEdit(match);
      setPendingOpenId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingOpenId]);

  function nameFor(uid: string | null) {
    if (!uid) return null;
    if (uid === user?.uid) return profile?.displayName ?? "Dir";
    if (uid === partner?.uid) return partner?.displayName ?? "Partner";
    return null;
  }

  const open = items.filter((i) => !i.done);
  const done = items.filter((i) => i.done);

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Todos</h1>
      <p className="mt-1 text-sm text-ink-400 dark:text-night-faint">Offene Punkte, die ihr erledigen wollt.</p>

      <form onSubmit={handleQuickAdd} className="mt-4 flex gap-2">
        <input
          type="text"
          placeholder="Was steht an?"
          value={quickText}
          onChange={(e) => setQuickText(e.target.value)}
          className="h-12 flex-1 rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
        />
        <button
          type="submit"
          disabled={!quickText.trim() || quickSubmitting}
          aria-label="Todo hinzufügen"
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-plum-700 text-cream-50 disabled:opacity-50"
        >
          {quickSubmitting ? (
            "…"
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </form>

      {loading ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
      ) : items.length === 0 ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Nichts offen – gut gemacht.</p>
      ) : (
        <>
          <ul className="mt-5 space-y-2">
            {open.map((t) => (
              <TodoRow
                key={t.id}
                todo={t}
                assignedName={nameFor(t.assignedToUid)}
                onToggle={() => patch(t.id, { done: true })}
                onAssign={(uid) => patch(t.id, { assignedToUid: uid })}
                onEdit={() => openEdit(t)}
                onRemove={() => removeTodo(t)}
                onComment={() => setCommentTodoId(t.id)}
                meUid={user?.uid ?? null}
                partnerUid={partner?.uid ?? null}
                meName={profile?.displayName}
                partnerName={partner?.displayName}
              />
            ))}
          </ul>
          {done.length > 0 && (
            <>
              <button
                onClick={() => setDoneOpen((v) => !v)}
                className="mt-6 flex w-full items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-ink-400 dark:text-night-faint"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`h-3 w-3 flex-shrink-0 transition-transform ${doneOpen ? "rotate-90" : ""}`}
                >
                  <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Erledigt ({done.length})
              </button>
              {doneOpen && (
                <ul className="mt-2 space-y-2">
                  {done.map((t) => (
                    <TodoRow
                      key={t.id}
                      todo={t}
                      assignedName={nameFor(t.assignedToUid)}
                      onToggle={() => patch(t.id, { done: false })}
                      onAssign={(uid) => patch(t.id, { assignedToUid: uid })}
                      onEdit={() => openEdit(t)}
                      onRemove={() => removeTodo(t)}
                      onComment={() => setCommentTodoId(t.id)}
                      meUid={user?.uid ?? null}
                      partnerUid={partner?.uid ?? null}
                      meName={profile?.displayName}
                      partnerName={partner?.displayName}
                    />
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}

      <Sheet open={sheetOpen} onClose={closeSheet} title="Todo bearbeiten">
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            placeholder="Was steht an?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <div>
            <p className="mb-1 text-xs font-medium text-ink-400 dark:text-night-faint">Fällig am (optional)</p>
            <div className="flex gap-1.5">
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="h-12 w-full rounded-xl border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
              />
              {dueDate && (
                <button
                  type="button"
                  onClick={() => setDueDate("")}
                  aria-label="Fälligkeitsdatum zurücksetzen"
                  className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-cream-200 text-ink-400 dark:border-night-border dark:text-night-faint"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
                    <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          {dueDate && (
            <div className="flex items-center justify-between rounded-xl bg-cream-100 px-4 py-3 dark:bg-night-surface-raised">
              <div>
                <p className="text-sm font-medium text-ink-900 dark:text-night-text">Auch im Kalender anzeigen</p>
                <p className="mt-0.5 text-xs text-ink-400 dark:text-night-faint">
                  Legt am Fälligkeitsdatum einen Termin an. Text und Datum bleiben synchron, egal wo du sie änderst.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowInCalendar((v) => !v)}
                aria-pressed={showInCalendar}
                className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${showInCalendar ? "bg-plum-700" : "bg-cream-200 dark:bg-night-border"}`}
              >
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] ${showInCalendar ? "left-6" : "left-1"}`} />
              </button>
            </div>
          )}
          <button
            type="submit"
            disabled={!text.trim() || submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
          >
            {submitting ? "Wird gespeichert…" : "Änderungen speichern"}
          </button>
        </form>
      </Sheet>

      <TodoCommentsSheet
        coupleId={couple?.id ?? null}
        todoId={commentTodoId}
        todoText={commentTodo?.text ?? null}
        onClose={() => setCommentTodoId(null)}
        meUid={user?.uid ?? null}
        meName={profile?.displayName ?? null}
        partnerName={partner?.displayName ?? null}
      />
    </main>
  );
}

function TodoCommentsSheet({
  coupleId,
  todoId,
  todoText,
  onClose,
  meUid,
  meName,
  partnerName,
}: {
  coupleId: string | null;
  todoId: string | null;
  todoText: string | null;
  onClose: () => void;
  meUid: string | null;
  meName: string | null;
  partnerName: string | null;
}) {
  // Gated on todoId rather than always-on, so Todos.tsx doesn't end up
  // with one live subscription per row just to show a comment count —
  // only the thread actually being viewed is subscribed.
  const { items: comments, loading, add, patch } = useCoupleCollection<TodoComment>(
    todoId ? coupleId : null,
    `todos/${todoId}/comments`,
    orderBy("createdAt", "asc")
  );
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Only the comment's own author can edit it (also enforced in
  // firestore.rules — this is just the UI side of that).
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);

  useEffect(() => {
    setEditingCommentId(null);
    setDraft("");
  }, [todoId]);

  function startEdit(c: TodoComment) {
    setEditingCommentId(c.id);
    setDraft(c.text);
  }

  function cancelEdit() {
    setEditingCommentId(null);
    setDraft("");
  }

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    if (submitting || !draft.trim() || !meUid || !meName) return;
    setSubmitting(true);
    try {
      if (editingCommentId) {
        await patch(editingCommentId, { text: draft.trim(), editedAt: serverTimestamp() });
        setEditingCommentId(null);
      } else {
        await add({ text: draft.trim(), createdByUid: meUid, createdByName: meName });
      }
      setDraft("");
    } finally {
      setSubmitting(false);
    }
  }

  function insertMention() {
    if (!partnerName) return;
    const tag = `@${partnerName}`;
    if (draft.includes(tag)) return;
    setDraft((prev) => (prev.trim() ? `${prev.trim()} ${tag} ` : `${tag} `));
  }

  return (
    <Sheet open={todoId !== null} onClose={onClose} title={todoText ?? "Kommentare"}>
      <div className="max-h-[50vh] space-y-3 overflow-y-auto">
        {loading ? (
          <p className="text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
        ) : comments.length === 0 ? (
          <p className="text-sm text-ink-400 dark:text-night-faint">Noch keine Kommentare.</p>
        ) : (
          comments.map((c) => {
            const isMine = c.createdByUid === meUid;
            const bubble = (
              <p
                className={`mt-0.5 inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-left text-sm ${
                  isMine ? "bg-plum-700 text-cream-50" : "bg-cream-100 text-ink-900 dark:bg-night-surface-raised dark:text-night-text"
                }`}
              >
                {c.text}
                {c.editedAt && <span className={`ml-1.5 text-[10px] ${isMine ? "text-cream-50/60" : "text-ink-400 dark:text-night-faint"}`}>(bearbeitet)</span>}
              </p>
            );
            return (
              <div key={c.id} className={isMine ? "text-right" : "text-left"}>
                <p className="text-xs font-semibold text-ink-400 dark:text-night-faint">{c.createdByName}</p>
                {isMine ? (
                  <button type="button" onClick={() => startEdit(c)} aria-label="Kommentar bearbeiten">
                    {bubble}
                  </button>
                ) : (
                  bubble
                )}
              </div>
            );
          })
        )}
      </div>
      {partnerName && (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={insertMention}
            className="flex-shrink-0 rounded-full border border-cream-200 px-2.5 py-1 text-xs font-medium text-plum-700 dark:border-night-border dark:text-night-accent"
          >
            @{partnerName}
          </button>
          <p className="text-xs text-ink-400 dark:text-night-faint">Erwähnt mit @{partnerName} benachrichtigt gezielt, auch wenn sonst aus.</p>
        </div>
      )}
      {editingCommentId && <p className="mt-3 text-xs font-medium text-plum-700 dark:text-night-accent">Kommentar wird bearbeitet…</p>}
      <form onSubmit={handleSend} className={editingCommentId ? "mt-1 flex gap-2" : "mt-2 flex gap-2"}>
        <input
          type="text"
          placeholder="z.B. Erledigt, oder eine Frage…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="h-12 flex-1 rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
        />
        {editingCommentId && (
          <button
            type="button"
            onClick={cancelEdit}
            aria-label="Bearbeiten abbrechen"
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-cream-200 text-ink-400 dark:border-night-border dark:text-night-faint"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
              <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <button
          type="submit"
          disabled={!draft.trim() || submitting}
          aria-label={editingCommentId ? "Kommentar speichern" : "Kommentar senden"}
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-plum-700 text-cream-50 disabled:opacity-50"
        >
          {submitting ? (
            "…"
          ) : editingCommentId ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="h-5 w-5">
              <path d="M5 12.5 9.5 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
              <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      </form>
    </Sheet>
  );
}

function TodoRow({
  todo,
  assignedName,
  onToggle,
  onAssign,
  onEdit,
  onRemove,
  onComment,
  meUid,
  partnerUid,
  meName,
  partnerName,
}: {
  todo: Todo;
  assignedName: string | null;
  onToggle: () => void;
  onAssign: (uid: string | null) => void;
  onEdit: () => void;
  onRemove: () => void;
  onComment: () => void;
  meUid: string | null;
  partnerUid: string | null;
  meName?: string;
  partnerName?: string;
}) {
  // Color reflects who it's assigned to (relative to the viewer, never a
  // fixed per-person color — "mine" and "partner's" swap depending on who's
  // looking), so a glance at the list tells you what's relevant to you
  // without reading every row.
  const assigneeColor =
    todo.assignedToUid === meUid
      ? { border: "border-plum-700", fill: "bg-plum-700" }
      : todo.assignedToUid === partnerUid
        ? { border: "border-gold-500", fill: "bg-gold-500" }
        : { border: "border-cream-200 dark:border-night-border", fill: "bg-ink-400 dark:bg-night-faint" };

  return (
    <li className="card rounded-xl p-3.5">
      <div className="flex items-center gap-3">
        <button
          onClick={onToggle}
          aria-label={todo.done ? "Als offen markieren" : "Als erledigt markieren"}
          className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 text-cream-50 ${
            todo.done ? `border-transparent ${assigneeColor.fill}` : assigneeColor.border
          }`}
        >
          {todo.done && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="h-3.5 w-3.5">
              <path d="M5 12.5 9.5 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
        <button
          onClick={onEdit}
          className={`min-w-0 flex-1 truncate text-left text-sm ${todo.done ? "text-ink-400 line-through dark:text-night-faint" : "text-ink-900 dark:text-night-text"}`}
        >
          {todo.text}
        </button>
        <button onClick={onComment} aria-label="Kommentare" className="relative flex-shrink-0 text-ink-400 dark:text-night-faint">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
            <path d="M4 5h16v11H8l-4 4V5Z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {!!todo.commentCount && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-plum-700 px-1 text-[10px] font-bold text-cream-50">
              {todo.commentCount}
            </span>
          )}
        </button>
        <button onClick={onEdit} aria-label="Bearbeiten" className="flex-shrink-0 text-ink-400 dark:text-night-faint">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
            <path d="M16.5 4.5 19.5 7.5 8 19H5v-3L16.5 4.5Z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button onClick={onRemove} aria-label="Löschen" className="flex-shrink-0 text-ink-400 dark:text-night-faint">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
            <path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-9 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <div className="mt-2 flex items-center gap-1.5 pl-9 text-xs">
        <span className="text-ink-400 dark:text-night-faint">{assignedName ? `Für ${assignedName}` : "Niemand zugewiesen"}</span>
        {todo.dueDate && (
          <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 font-medium ${isOverdue(todo) ? "bg-garnet-100 text-garnet-600 dark:bg-garnet-600/15" : "bg-cream-100 text-ink-600 dark:bg-night-surface-raised dark:text-night-muted"}`}>
            {new Date(todo.dueDate + "T00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
          </span>
        )}
        {todo.linkedEventId && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5 flex-shrink-0 text-plum-700 dark:text-night-accent" aria-label="Auch im Kalender">
            <rect x="4" y="5" width="16" height="16" rx="2" />
            <path d="M8 3v4M16 3v4M4 10h16" strokeLinecap="round" />
          </svg>
        )}
        <button onClick={() => onAssign(todo.assignedToUid === meUid ? null : meUid)} className="ml-auto text-plum-700 underline-offset-2 hover:underline dark:text-night-accent">
          {meName ?? "Ich"}
        </button>
        <span className="text-ink-200">·</span>
        <button onClick={() => onAssign(todo.assignedToUid === partnerUid ? null : partnerUid)} className="text-plum-700 underline-offset-2 hover:underline dark:text-night-accent">
          {partnerName ?? "Partner"}
        </button>
      </div>
    </li>
  );
}
